import * as THREE from 'three'
import nebulaVertex from '@/shaders/nebula.vert.glsl?raw'
import nebulaFragment from '@/shaders/nebula.frag.glsl?raw'
import starVertex from '@/shaders/star.vert.glsl?raw'
import starFragment from '@/shaders/star.frag.glsl?raw'
import { withNoise } from '@/_utilities/shader'
import { palette, nebulaColours } from '@/_constants/palette'
import { sceneConfig } from '@/_constants/scene-config'
import { damp } from '@/_utilities/math'
import type { QualityProfile } from '@/_types/scene'

/**
 * The entire deep-space backdrop: a domain-warped nebula sphere, a twinkling
 * starfield, a cloud of slowly drifting ambient particles, and an occasional
 * shooting star. Everything here sits behind the solar system and never reacts
 * to pointer input.
 */
export class Background {
  public readonly object: THREE.Group

  private readonly nebulaMaterial: THREE.ShaderMaterial
  private readonly starMaterial: THREE.ShaderMaterial
  private readonly ambientMaterial: THREE.ShaderMaterial
  private readonly ambientPoints: THREE.Points
  private readonly shootingStar: THREE.Line
  private readonly shootingStarMaterial: THREE.LineBasicMaterial
  private readonly shootingStarDirection: THREE.Vector3
  private psychedelicTarget: number
  private psychedelicCurrent: number
  private nextShootingStarAt: number
  private shootingStarLife: number

  /**
   * @param {QualityProfile} quality - The resolved device quality profile, used
   * to scale star and particle counts and the point pixel ratio.
   */
  public constructor(quality: QualityProfile) {
    this.object = new THREE.Group()
    this.psychedelicTarget = 0
    this.psychedelicCurrent = 0
    this.nextShootingStarAt = sceneConfig.motion.shootingStarIntervalSeconds
    this.shootingStarLife = 0
    this.shootingStarDirection = new THREE.Vector3()

    this.nebulaMaterial = this.createNebulaMaterial()
    const nebulaGeometry = new THREE.SphereGeometry(2000, 64, 64)
    const nebula = new THREE.Mesh(nebulaGeometry, this.nebulaMaterial)
    this.object.add(nebula)

    this.starMaterial = this.createPointMaterial(quality.pixelRatio, 1.4)
    const starfield = this.createStarfield(quality.starCount)
    this.object.add(starfield)

    this.ambientMaterial = this.createPointMaterial(quality.pixelRatio, 0.7)
    this.ambientPoints = this.createAmbientParticles(quality.ambientParticleCount)
    this.object.add(this.ambientPoints)

    this.shootingStarMaterial = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      opacity: 0,
    })
    this.shootingStar = this.createShootingStar()
    this.object.add(this.shootingStar)
  }

  /**
   * Advance every animated element of the backdrop.
   *
   * @param {number} elapsedSeconds - Total elapsed time since start.
   * @param {number} deltaSeconds - Time since the previous frame.
   * @returns {void}
   */
  public update(elapsedSeconds: number, deltaSeconds: number): void {
    this.psychedelicCurrent = damp(this.psychedelicCurrent, this.psychedelicTarget, 0.04, deltaSeconds)

    this.nebulaMaterial.uniforms.uTime.value = elapsedSeconds
    this.nebulaMaterial.uniforms.uPsychedelic.value = this.psychedelicCurrent
    this.starMaterial.uniforms.uTime.value = elapsedSeconds
    this.ambientMaterial.uniforms.uTime.value = elapsedSeconds

    this.ambientPoints.rotation.y += deltaSeconds * 0.012
    this.ambientPoints.rotation.x += deltaSeconds * 0.004

    this.updateShootingStar(elapsedSeconds, deltaSeconds)
  }

  /**
   * Force an immediate, longer-lived comet streak (used by the idle watcher).
   *
   * @returns {void}
   */
  public triggerComet(): void {
    this.launchShootingStar()
    this.shootingStarLife = 2.2
  }

  /**
   * Set how intense the psychedelic colour cycling of the nebula should be.
   *
   * @param {number} amount - Target intensity in the 0..1 range.
   * @returns {void}
   */
  public setPsychedelic(amount: number): void {
    this.psychedelicTarget = amount
  }

  /**
   * Release every GPU resource owned by the backdrop.
   *
   * @returns {void}
   */
  public dispose(): void {
    this.object.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.Points || child instanceof THREE.Line) {
        child.geometry.dispose()
      }
    })
    this.nebulaMaterial.dispose()
    this.starMaterial.dispose()
    this.ambientMaterial.dispose()
    this.shootingStarMaterial.dispose()
  }

  /**
   * Build the nebula sphere material.
   *
   * @returns {THREE.ShaderMaterial} The configured nebula material.
   */
  private createNebulaMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      vertexShader: nebulaVertex,
      fragmentShader: withNoise(nebulaFragment),
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uPsychedelic: { value: 0 },
        uDeepNear: { value: new THREE.Color(palette.deepSpaceNear) },
        uDeepFar: { value: new THREE.Color(palette.deepSpaceFar) },
        uNebulaA: { value: new THREE.Color(nebulaColours[0]) },
        uNebulaB: { value: new THREE.Color(nebulaColours[1]) },
        uNebulaC: { value: new THREE.Color(nebulaColours[2]) },
        uNebulaD: { value: new THREE.Color(nebulaColours[3]) },
      },
    })
  }

  /**
   * Build a shared point-sprite material for stars or ambient particles.
   *
   * @param {number} pixelRatio - The device pixel ratio to scale point sizes by.
   * @param {number} twinkleSpeed - How quickly the points shimmer.
   * @returns {THREE.ShaderMaterial} The configured point material.
   */
  private createPointMaterial(pixelRatio: number, twinkleSpeed: number): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      vertexShader: starVertex,
      fragmentShader: starFragment,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uPixelRatio: { value: pixelRatio },
        uTwinkleSpeed: { value: twinkleSpeed },
        uOpacity: { value: 1 },
      },
    })
  }

  /**
   * Generate the starfield geometry as a thick spherical shell of points.
   *
   * @param {number} count - The number of stars to scatter.
   * @returns {THREE.Points} The starfield points object.
   */
  private createStarfield(count: number): THREE.Points {
    const positions = new Float32Array(count * 3)
    const colours = new Float32Array(count * 3)
    const sizes = new Float32Array(count)
    const phases = new Float32Array(count)
    const starTints = [palette.textPrimary, palette.secondaryGlow, palette.primaryGlow, palette.nebulaGold]

    for (let index = 0; index < count; index += 1) {
      const direction = new THREE.Vector3()
        .randomDirection()
        .multiplyScalar(sceneConfig.starfield.radius * (0.7 + Math.random() * 0.3))
      positions[index * 3] = direction.x
      positions[index * 3 + 1] = direction.y
      positions[index * 3 + 2] = direction.z

      const tint = new THREE.Color(starTints[Math.floor(Math.random() * starTints.length)])
      tint.multiplyScalar(0.6 + Math.random() * 0.4)
      colours[index * 3] = tint.r
      colours[index * 3 + 1] = tint.g
      colours[index * 3 + 2] = tint.b

      sizes[index] =
        sceneConfig.starfield.minSize +
        Math.random() * (sceneConfig.starfield.maxSize - sceneConfig.starfield.minSize)
      phases[index] = Math.random() * Math.PI * 2
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('aColour', new THREE.BufferAttribute(colours, 3))
    geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1))
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1))
    return new THREE.Points(geometry, this.starMaterial)
  }

  /**
   * Generate the closer cloud of slowly drifting ambient particles.
   *
   * @param {number} count - The number of ambient particles.
   * @returns {THREE.Points} The ambient particle points object.
   */
  private createAmbientParticles(count: number): THREE.Points {
    const positions = new Float32Array(count * 3)
    const colours = new Float32Array(count * 3)
    const sizes = new Float32Array(count)
    const phases = new Float32Array(count)
    const tints = [palette.primaryGlow, palette.secondaryGlow, palette.nebulaMagenta]

    for (let index = 0; index < count; index += 1) {
      const radius = sceneConfig.particles.ambientRadius * Math.cbrt(Math.random())
      const direction = new THREE.Vector3().randomDirection().multiplyScalar(radius)
      positions[index * 3] = direction.x
      positions[index * 3 + 1] = direction.y
      positions[index * 3 + 2] = direction.z

      const tint = new THREE.Color(tints[Math.floor(Math.random() * tints.length)])
      tint.multiplyScalar(0.4 + Math.random() * 0.4)
      colours[index * 3] = tint.r
      colours[index * 3 + 1] = tint.g
      colours[index * 3 + 2] = tint.b

      sizes[index] = 1.4 + Math.random() * 2.6
      phases[index] = Math.random() * Math.PI * 2
    }

    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('aColour', new THREE.BufferAttribute(colours, 3))
    geometry.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1))
    geometry.setAttribute('aPhase', new THREE.BufferAttribute(phases, 1))
    return new THREE.Points(geometry, this.ambientMaterial)
  }

  /**
   * Build the reusable shooting-star line (head bright, tail transparent).
   *
   * @returns {THREE.Line} The shooting-star line object.
   */
  private createShootingStar(): THREE.Line {
    const positions = new Float32Array([0, 0, 0, 0, 0, 0])
    const head = new THREE.Color(palette.textPrimary)
    const tail = new THREE.Color('#000000')
    const colours = new Float32Array([head.r, head.g, head.b, tail.r, tail.g, tail.b])
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geometry.setAttribute('color', new THREE.BufferAttribute(colours, 3))
    const line = new THREE.Line(geometry, this.shootingStarMaterial)
    line.visible = false
    line.frustumCulled = false
    return line
  }

  /**
   * Drive the shooting-star launch timer and animate an active streak.
   *
   * @param {number} elapsedSeconds - Total elapsed time since start.
   * @param {number} deltaSeconds - Time since the previous frame.
   * @returns {void}
   */
  private updateShootingStar(elapsedSeconds: number, deltaSeconds: number): void {
    if (this.shootingStarLife > 0) {
      this.shootingStarLife -= deltaSeconds
      const headSpeed = 520
      const positionAttribute = this.shootingStar.geometry.getAttribute('position') as THREE.BufferAttribute
      const headX = positionAttribute.getX(0) + this.shootingStarDirection.x * headSpeed * deltaSeconds
      const headY = positionAttribute.getY(0) + this.shootingStarDirection.y * headSpeed * deltaSeconds
      const headZ = positionAttribute.getZ(0) + this.shootingStarDirection.z * headSpeed * deltaSeconds
      positionAttribute.setXYZ(0, headX, headY, headZ)
      positionAttribute.setXYZ(
        1,
        headX - this.shootingStarDirection.x * 90,
        headY - this.shootingStarDirection.y * 90,
        headZ - this.shootingStarDirection.z * 90,
      )
      positionAttribute.needsUpdate = true
      this.shootingStarMaterial.opacity = Math.max(this.shootingStarLife, 0) * 1.1
      if (this.shootingStarLife <= 0) {
        this.shootingStar.visible = false
      }
      return
    }

    if (elapsedSeconds >= this.nextShootingStarAt) {
      this.launchShootingStar()
      this.nextShootingStarAt =
        elapsedSeconds + sceneConfig.motion.shootingStarIntervalSeconds * (0.6 + Math.random())
    }
  }

  /**
   * Position and arm a fresh shooting-star streak somewhere in the far field.
   *
   * @returns {void}
   */
  private launchShootingStar(): void {
    const start = new THREE.Vector3(
      (Math.random() - 0.5) * 900,
      260 + Math.random() * 220,
      (Math.random() - 0.5) * 900,
    )
    this.shootingStarDirection
      .set(Math.random() - 0.5, -0.4 - Math.random() * 0.4, Math.random() - 0.5)
      .normalize()

    const positionAttribute = this.shootingStar.geometry.getAttribute('position') as THREE.BufferAttribute
    positionAttribute.setXYZ(0, start.x, start.y, start.z)
    positionAttribute.setXYZ(
      1,
      start.x - this.shootingStarDirection.x * 90,
      start.y - this.shootingStarDirection.y * 90,
      start.z - this.shootingStarDirection.z * 90,
    )
    positionAttribute.needsUpdate = true
    this.shootingStarLife = 1.1
    this.shootingStar.visible = true
    this.shootingStarMaterial.opacity = 1
  }
}
